/**
 * Step 2 — the gateway verdict: one card — "The gateway is terrible when …",
 * judged per gateway on its own drops today — blocked for the rest of the
 * day, in this container or (14/10/2026) the whole gateway in every container
 * of the country. The mechanics live in one ⓘ; the dead-route rule is one
 * click away.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Ban, Skull } from 'lucide-react';

import { Button, HintDot } from '@/components/ui';

import type { DraftFlatRule, DropGenPlanDetail } from '../../../../drop-generator.types';
import { ChoiceChips } from '../../general/controls/ChoiceChips';
import { DEAD_ROUTE_ROWS } from '../../state/satellites/gateway-verdict-draft';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { OnlyWhen } from '../card/only-when/OnlyWhen';
import { VerdictCard } from '../card/VerdictCard';
import { HistoryLine } from '../history/HistoryLine';
import { withSuggestion } from '../history/history-words';
import { ReplayPanel } from '../replay/ReplayPanel';
import { RightNowLine } from '../source/RightNowLine';
import { rowsForSource } from '../source/source-words';

const HINT =
  "Each gateway of the pool is judged on its own — on the drops this container sent through it today (the same numbers, the list's settle time, a sample of its own, DROP tags), or on the gateway's own tracker stats over the date range (the formulas' metrics, GATEWAY tags). A gateway judged terrible is blocked for the rest of the day — in this container (its unsent drafts move to the pool's other gateways), or, when you pick “in every container”, the whole gateway in every container of the country, even those with no gateway rule; tomorrow it is back. Under the sample, or with a metric the tracker has not reported, it is inconclusive — never blocked on missing data. Only gateways that sent today are judged. The switch on this card turns the checks on; the list verdict does not.";

const SCOPES = [
  { value: 'CONTAINER', label: 'in this container' },
  { value: 'COUNTRY', label: 'in every container' },
] as const;

const MOVED = {
  CONTAINER: "its unsent drafts move to the pool's other gateways · back in the pool tomorrow",
  COUNTRY: "the whole gateway leaves every container of the country today — their unsent drafts move to their other gateways · back tomorrow",
} as const;

export const GatewayVerdictStep: FC<{
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
}> = ({ editor, plan, containerId }) => {
  const { gatewayVerdict: gv, verdict } = editor.draft;
  const patchRule = (patch: Partial<DraftFlatRule>) => editor.gatewayVerdictPatch({ rule: { ...gv.rule, ...patch } });
  const onDrops = gv.source.kind === 'drops';
  return (
    <div className={gv.enabled ? 'flex flex-col gap-3' : 'flex flex-col gap-3 opacity-60'}>
      {!gv.enabled ? <p className="rounded-md border border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">Off — gateways are not judged until this is on.</p> : null}
      <VerdictCard
        card="gatewayVerdict"
        source={gv.source}
        onSource={(source) =>
          editor.gatewayVerdictPatch({
            source,
            rule: {
              ...gv.rule,
              rows: rowsForSource(gv.rule.rows, source.kind),
            },
          })
        }
        icon={Ban}
        lead={
          <>
            The gateway is <span className="text-destructive">terrible</span>
          </>
        }
        aside={<HintDot label={HINT} />}
        rule={gv.rule}
        onPatch={patchRule}
        starter={onDrops ? { metric: 'deliveredRate', op: '<', value: 0.6 } : { metric: 'cr', op: '<', value: 1 }}
        footer={
          <OnlyWhen
            scope="gateway"
            sample={{ value: gv.minSampleSent, onChange: (v) => editor.gatewayVerdictPatch({ minSampleSent: Math.max(0, v) }), unit: onDrops ? 'sms' : 'visits' }}
            settle={onDrops ? { value: verdict.evaluationDelayMin, onChange: (evaluationDelayMin) => editor.verdictPatch({ evaluationDelayMin }), shared: true } : undefined}
            times={onDrops ? { value: gv.timesN, onChange: (timesN) => editor.gatewayVerdictPatch({ timesN }), mode: gv.timesMode, onMode: (timesMode) => editor.gatewayVerdictPatch({ timesMode }) } : undefined}
          />
        }
        outcome={{
          tone: 'destructive',
          icon: Ban,
          label: 'blocked for the day',
        }}
      >
        <ChoiceChips options={SCOPES} value={gv.blockScope} onChange={(blockScope) => editor.gatewayVerdictPatch({ blockScope })} ariaLabel="Where a terrible gateway is blocked" />
        <p className="text-xs text-muted-foreground">{MOVED[gv.blockScope]}</p>
        {!gv.rule.advanced && gv.rule.rows.length === 0 && onDrops ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            title="Visits = 0 and SMS Clicks = 0 — a gateway nobody reached"
            onClick={() =>
              patchRule({
                join: 'AND',
                rows: DEAD_ROUTE_ROWS.map((r) => ({ ...r })),
              })
            }
          >
            <Skull className="mr-1.5 h-3.5 w-3.5" /> Start from the dead-route rule
          </Button>
        ) : null}
        {onDrops ? (
          <HistoryLine countryKey={plan.countryKey} id={containerId ?? editor.draft.masterId} kind="route" which="terrible" onUse={(s) => patchRule({ rows: withSuggestion(gv.rule.rows, s) })} />
        ) : (
          <RightNowLine countryKey={plan.countryKey} containerId={containerId} card="gatewayVerdict" source={gv.source} rule={gv.rule} minSampleSent={gv.minSampleSent} />
        )}
      </VerdictCard>
      <ReplayPanel kind="gateway" draft={editor.draft} plan={plan} containerId={containerId} />
    </div>
  );
};

/** The step card's one-line summary. */
export function gatewayVerdictSummary(editor: ContainerEditor): string {
  const { enabled, rule, minSampleSent } = editor.draft.gatewayVerdict;
  if (!enabled) return 'off — gateways are not judged';
  if (rule.advanced) return 'nested rule (set through the API)';
  if (rule.rows.length === 0) return 'no rule — gateways are never judged';
  // 20/10/2026 — the guards are optional: a sample is named only when one was added.
  const sample = minSampleSent > 0 ? ` · from ${minSampleSent.toLocaleString('en-US')} ${editor.draft.gatewayVerdict.source.kind === 'drops' ? 'SMS' : 'unique visits'}` : '';
  const where = editor.draft.gatewayVerdict.blockScope === 'COUNTRY' ? ' in every container' : '';
  const { timesN, timesMode } = editor.draft.gatewayVerdict;
  const times = timesN > 1 ? ` · on ${timesN} of its drops${timesMode === 'ROW' ? ' in a row' : ''}` : '';
  return `${rule.rows.length} condition${rule.rows.length === 1 ? '' : 's'}${sample}${times} · a terrible gateway is blocked for the day${where}`;
}
